import { useTranslation } from 'react-i18next';
import { Skeleton, Typography } from '@mui/material';
import React, { lazy } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { upperFirst } from 'lodash';
import { TopBar } from '../ProductsPage';
import { UpcomingProductCard } from './UpcomingProduct/UpcomingProductCard';
import { RemovedProductCard } from './UpcomingProduct/RemovedProductCard';
import { OverviewGroup, useUpcomingProductOverview } from './UpcomingProduct/useUpcomingProductOverview';

// Loaded only for `?testPage`, so the fixtures stay out of the main bundle.
const UpcomingProductsTestPage = lazy(() => import('./testPage'));

type ViewProps = {
  isLoading: boolean;
  isError?: boolean;
  isS4DError?: boolean;
  groups: OverviewGroup[];
  getIngredientName: (ingredientId: number) => string | null | undefined;
  notice?: React.ReactNode;
};

export const UpcomingProductsView: React.FC<ViewProps> = ({
  isLoading,
  isError,
  isS4DError,
  groups,
  getIngredientName,
  notice,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <TopBar>
        <Typography variant="h1">{t('nav.upcomingProducts')}</Typography>
      </TopBar>
      {notice}
      {isS4DError && <Notice color="warning.main">{t('products.upcoming.s4dUnavailable')}</Notice>}
      {isLoading ? (
        <CardRow>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} variant="rounded" width={340} height={320} />
          ))}
        </CardRow>
      ) : isError ? (
        <Typography color="error">{t('products.upcoming.loadFailed')}</Typography>
      ) : groups.length === 0 ? (
        <Typography color="text.secondary">{t('products.upcoming.noChanges')}</Typography>
      ) : (
        groups.map((group) => (
          <Group key={group.key}>
            <Typography variant="h3" component="h2">
              {group.date ? upperFirst(group.date.format('dddd D.M.YYYY')) : t('products.upcoming.testMenuOnly')}
            </Typography>
            <CardRow>
              {group.added.map((card) => (
                <UpcomingProductCard
                  key={`added-${card.product.productId}`}
                  {...card}
                  getIngredientName={getIngredientName}
                />
              ))}
              {group.removed.map((card) => (
                <RemovedProductCard key={`removed-${card.productId}`} {...card} />
              ))}
            </CardRow>
          </Group>
        ))
      )}
    </>
  );
};

const UpcomingProducts: React.FC = () => {
  const overview = useUpcomingProductOverview();

  return <UpcomingProductsView {...overview} />;
};

// Decided in a wrapper so the real page and its queries never mount on the test page.
export const ProductTimelinePage: React.FC = () => {
  const { search } = useLocation();

  return new URLSearchParams(search).has('testPage') ? <UpcomingProductsTestPage /> : <UpcomingProducts />;
};

const Notice = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const CardRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export default ProductTimelinePage;
